export type ThingsToDoCategory =
  | 'parks'
  | 'indoor-play'
  | 'splash-pads'
  | 'libraries'
  | 'family-fun';

export interface ThingsToDoPlace {
  id: string;
  name: string;
  category: ThingsToDoCategory;
  location: string;
  address: string;
  description: string;
  ageRange: string;
  cost: string;
  image: string;
  mapLink: string;
  website?: string;
  momTip?: string;
  tags: string[];
}

export const categoryConfig: Record<
  ThingsToDoCategory,
  { label: string; icon: string; color: string; bg: string; accent: string }
> = {
  parks: {
    label: 'Parks & Trails',
    icon: 'ri-leaf-line',
    color: 'text-emerald-700',
    bg: 'bg-emerald-50',
    accent: 'bg-emerald-100',
  },
  'indoor-play': {
    label: 'Indoor Play',
    icon: 'ri-game-line',
    color: 'text-violet-700',
    bg: 'bg-violet-50',
    accent: 'bg-violet-100',
  },
  'splash-pads': {
    label: 'Splash Pads & Pools',
    icon: 'ri-water-flash-line',
    color: 'text-sky-700',
    bg: 'bg-sky-50',
    accent: 'bg-sky-100',
  },
  libraries: {
    label: 'Libraries',
    icon: 'ri-book-open-line',
    color: 'text-amber-700',
    bg: 'bg-amber-50',
    accent: 'bg-amber-100',
  },
  'family-fun': {
    label: 'Family Fun',
    icon: 'ri-heart-3-line',
    color: 'text-rose-700',
    bg: 'bg-rose-50',
    accent: 'bg-rose-100',
  },
};

export const places: ThingsToDoPlace[] = [
  // PARKS & TRAILS
  {
    id: 'td-1',
    name: 'Tribble Mill Park',
    category: 'parks',
    location: 'Lawrenceville, GA',
    address: '3600 Tribble Mill Pkwy, Lawrenceville, GA 30045',
    description:
      'One of Gwinnett\'s most beloved parks with two lakes, scenic trails, fishing docks, and a great playground. Perfect for long family walks and picnics.',
    ageRange: 'All ages',
    cost: 'Free',
    image:
      'https://public.readdy.ai/ai/img_res/f8ec307cd5e238edb8e9a567666bfbc0.jpg',
    mapLink: 'https://maps.google.com/?q=Tribble+Mill+Park+Lawrenceville+GA',
    website: 'https://www.gwinnettcounty.com',
    momTip: 'Pack a picnic — the lake-view area is shaded and beautiful.',
    tags: ['Picnic', 'Trails', 'Playground', 'Lake'],
  },
  {
    id: 'td-2',
    name: 'George Pierce Park',
    category: 'parks',
    location: 'Suwanee, GA',
    address: '55 Buford Hwy, Suwanee, GA 30024',
    description:
      'A massive community park with miles of trails, playgrounds, sports fields, and a beautiful creek. One of the most family-friendly parks in the entire county.',
    ageRange: 'All ages',
    cost: 'Free',
    image:
      'https://readdy.ai/api/search-image?query=large%20family%20park%20with%20wide%20open%20green%20fields%20children%20playground%20wooden%20structures%20paved%20trail%20system%20clean%20well%20maintained%20Georgia%20park%20morning%20light%20families%20walking%20dogs&width=800&height=520&seq=ttd_p2&orientation=landscape',
    mapLink: 'https://maps.google.com/?q=George+Pierce+Park+Suwanee+GA',
    momTip: 'Early morning walks here are magical. Great creek spot for kids.',
    tags: ['Trails', 'Creek', 'Large Playground', 'Dog Friendly'],
  },
  {
    id: 'td-3',
    name: 'Vines Park',
    category: 'parks',
    location: 'Loganville, GA',
    address: '3500 Oak Grove Rd, Loganville, GA 30052',
    description:
      'A hidden gem with a great playground, nature trails, a pond, and open green spaces. Excellent spot for toddler playdates and family morning outings.',
    ageRange: 'All ages',
    cost: 'Free',
    image:
      'https://readdy.ai/api/search-image?query=community%20park%20with%20colorful%20playground%20equipment%20pond%20with%20ducks%20nature%20trail%20through%20trees%20open%20grassy%20spaces%20families%20relaxing%20beautiful%20spring%20day%20Georgia&width=800&height=520&seq=ttd_p3&orientation=landscape',
    mapLink: 'https://maps.google.com/?q=Vines+Park+Loganville+GA',
    momTip: 'Feed the ducks at the pond — toddlers absolutely love it.',
    tags: ['Pond', 'Playground', 'Nature Trails'],
  },
  {
    id: 'td-4',
    name: 'Sugar Hill Greenway Trail',
    category: 'parks',
    location: 'Sugar Hill, GA',
    address: 'Sugar Hill Greenway, Sugar Hill, GA 30518',
    description:
      'A paved greenway trail perfect for stroller walks, bike rides, and casual runs. Connects multiple neighborhoods and passes through shaded wooded areas.',
    ageRange: 'All ages',
    cost: 'Free',
    image:
      'https://readdy.ai/api/search-image?query=paved%20greenway%20trail%20through%20wooded%20Georgia%20neighborhood%20dappled%20sunlight%20through%20trees%20mother%20pushing%20stroller%20joggers%20cyclists%20peaceful%20suburban%20trail%20community%20path&width=800&height=520&seq=ttd_p4&orientation=landscape',
    mapLink: 'https://maps.google.com/?q=Sugar+Hill+Greenway+Trail+GA',
    momTip: 'Stroller-friendly the entire way. Great for morning walks.',
    tags: ['Stroller Friendly', 'Paved Trail', 'Biking'],
  },
  {
    id: 'td-5',
    name: 'Rabbit Hill Park',
    category: 'parks',
    location: 'Sugar Hill, GA',
    address: '4650 Nelson Brogdon Blvd, Sugar Hill, GA 30518',
    description:
      'A local favorite for Sugar Hill families with playgrounds, walking trails, and open fields. Hosts community events throughout the year.',
    ageRange: 'All ages',
    cost: 'Free',
    image:
      'https://readdy.ai/api/search-image?query=neighborhood%20park%20with%20large%20climbing%20structure%20slides%20open%20lawn%20families%20playing%20frisbee%20sunny%20Georgia%20afternoon%20children%20running%20around%20community%20park%20shade%20trees&width=800&height=520&seq=ttd_p5&orientation=landscape',
    mapLink: 'https://maps.google.com/?q=Rabbit+Hill+Park+Sugar+Hill+GA',
    momTip: 'Great for after-school hangouts. Kids love the multi-level play structure.',
    tags: ['Playground', 'Open Fields', 'Community Events'],
  },
  // INDOOR PLAY
  {
    id: 'td-6',
    name: 'Sky Zone Suwanee',
    category: 'indoor-play',
    location: 'Suwanee, GA',
    address: '2700 Lawrenceville Suwanee Rd, Suwanee, GA 30024',
    description:
      'Massive indoor trampoline park with dodgeball, foam pits, obstacle courses, and a kids-only Toddler Time zone. A total energy burner for all ages.',
    ageRange: 'Ages 2 and up',
    cost: 'Paid',
    image:
      'https://readdy.ai/api/search-image?query=indoor%20trampoline%20park%20with%20colorful%20foam%20pit%20connected%20trampolines%20children%20jumping%20foam%20blocks%20toddler%20area%20bright%20colorful%20lights%20fun%20energetic%20indoor%20play%20facility&width=800&height=520&seq=ttd_i1&orientation=landscape',
    mapLink: 'https://maps.google.com/?q=Sky+Zone+Suwanee+GA',
    website: 'https://www.skyzone.com',
    momTip: 'Toddler Time (weekday mornings) is amazing — way less crowded.',
    tags: ['Trampolines', 'Foam Pit', 'Obstacle Course', 'Toddler Time'],
  },
  {
    id: 'td-7',
    name: 'All American Fun Park',
    category: 'indoor-play',
    location: 'Lawrenceville, GA',
    address: '2855 Buford Hwy, Duluth, GA 30096',
    description:
      'Go-karts, laser tag, mini golf, arcade games, and batting cages. A classic Georgia entertainment center that the whole family will love.',
    ageRange: 'Ages 3 and up',
    cost: 'Paid',
    image:
      'https://readdy.ai/api/search-image?query=family%20entertainment%20center%20with%20colorful%20arcade%20games%20go%20kart%20track%20indoor%20mini%20golf%20bright%20neon%20lighting%20children%20and%20families%20having%20fun%20indoor%20play%20center%20Georgia&width=800&height=520&seq=ttd_i2&orientation=landscape',
    mapLink: 'https://maps.google.com/?q=All+American+Fun+Park+Duluth+GA',
    momTip: 'Grab the family fun pack — much better value than paying per ride.',
    tags: ['Go-Karts', 'Arcade', 'Laser Tag', 'Mini Golf'],
  },
  {
    id: 'td-8',
    name: 'Jump Street Trampoline Park',
    category: 'indoor-play',
    location: 'Norcross, GA',
    address: '1500 Northbrook Pkwy, Norcross, GA 30093',
    description:
      'Wall-to-wall trampolines, a dedicated toddler zone, basketball slam dunk lanes, and dodgeball courts. Great for rainy days when the kids need to burn energy.',
    ageRange: 'Ages 2 and up',
    cost: 'Paid',
    image:
      'https://readdy.ai/api/search-image?query=indoor%20trampoline%20park%20wall%20to%20wall%20jumping%20surfaces%20basketball%20hoop%20slam%20dunk%20lanes%20colorful%20padding%20kids%20bouncing%20bright%20cheerful%20colors%20energetic%20atmosphere%20play%20area&width=800&height=520&seq=ttd_i3&orientation=landscape',
    mapLink: 'https://maps.google.com/?q=Jump+Street+Norcross+GA',
    momTip: 'Grip socks are required — buy them once and bring them every visit.',
    tags: ['Trampolines', 'Toddler Zone', 'Dodgeball', 'Basketball'],
  },
  {
    id: 'td-9',
    name: 'Catch Air Gwinnett',
    category: 'indoor-play',
    location: 'Lawrenceville, GA',
    address: '2825 Buford Hwy, Duluth, GA 30096',
    description:
      'Soft play area, bounce houses, slides, and climbing structures perfect for toddlers and young kids. A calm, safe, and clean indoor play space designed for little ones.',
    ageRange: 'Ages 0–10 yrs',
    cost: 'Paid',
    image:
      'https://readdy.ai/api/search-image?query=colorful%20indoor%20soft%20play%20area%20for%20toddlers%20with%20inflatable%20bounce%20house%20large%20padded%20slides%20ball%20pit%20climbing%20structures%20safe%20clean%20play%20environment%20mothers%20watching%20children&width=800&height=520&seq=ttd_i4&orientation=landscape',
    mapLink: 'https://maps.google.com/?q=Catch+Air+Duluth+GA',
    momTip: 'Great for under-5 crowd. Quieter on weekday mornings.',
    tags: ['Soft Play', 'Bounce Houses', 'Toddler Friendly', 'Safe'],
  },
  // SPLASH PADS & POOLS
  {
    id: 'td-10',
    name: 'Bethesda Park Aquatic Center',
    category: 'splash-pads',
    location: 'Lawrenceville, GA',
    address: '225 Bethesda Church Rd, Lawrenceville, GA 30044',
    description:
      'Indoor and outdoor pools, a splash pad area, waterslides, and a zero-entry area for little ones. A full water day destination for the whole family.',
    ageRange: 'All ages',
    cost: 'Paid',
    image:
      'https://readdy.ai/api/search-image?query=outdoor%20community%20aquatic%20center%20with%20water%20slides%20zero%20entry%20pool%20splash%20pad%20area%20families%20swimming%20sunny%20Georgia%20summer%20day%20lifeguards%20on%20duty%20kids%20playing%20in%20water%20clean%20facility&width=800&height=520&seq=ttd_s1&orientation=landscape',
    mapLink: 'https://maps.google.com/?q=Bethesda+Park+Aquatic+Center+Lawrenceville+GA',
    momTip: 'Get there early on weekends — it fills up fast in summer.',
    tags: ['Waterslides', 'Zero-Entry Pool', 'Splash Pad', 'Indoor Pool'],
  },
  {
    id: 'td-11',
    name: 'Sugar Hill Splash Pad at E Center',
    category: 'splash-pads',
    location: 'Sugar Hill, GA',
    address: '6038 Cumming Hwy, Sugar Hill, GA 30518',
    description:
      'Free outdoor splash pad right in Sugar Hill. Water jets, sprinklers, and a safe play surface for toddlers and young kids. Open seasonally — no ticket needed.',
    ageRange: 'Ages 1–10 yrs',
    cost: 'Free',
    image:
      'https://readdy.ai/api/search-image?query=free%20outdoor%20splash%20pad%20with%20water%20jets%20sprinklers%20children%20playing%20in%20clean%20water%20paved%20surface%20sunny%20summer%20day%20colorful%20water%20play%20equipment%20toddlers%20running%20through%20sprays&width=800&height=520&seq=ttd_s2&orientation=landscape',
    mapLink: 'https://maps.google.com/?q=Sugar+Hill+E+Center+Splash+Pad+GA',
    momTip: 'Bring a towel, sunscreen, and a change of clothes — they get soaked!',
    tags: ['Free', 'Splash Pad', 'Toddler Friendly', 'Seasonal'],
  },
  {
    id: 'td-12',
    name: 'Suwanee Creek Greenway Splash Zone',
    category: 'splash-pads',
    location: 'Suwanee, GA',
    address: 'Suwanee Town Center, 330 Town Center Ave, Suwanee, GA 30024',
    description:
      'Interactive water feature in Suwanee Town Center perfect for kids to cool off. Surrounded by green space, restaurants, and family-friendly atmosphere.',
    ageRange: 'Ages 1–8 yrs',
    cost: 'Free',
    image:
      'https://readdy.ai/api/search-image?query=town%20center%20outdoor%20water%20feature%20interactive%20fountains%20children%20playing%20in%20jets%20of%20water%20on%20bright%20summer%20day%20surrounded%20by%20shops%20restaurants%20families%20sitting%20on%20benches%20nearby&width=800&height=520&seq=ttd_s3&orientation=landscape',
    mapLink: 'https://maps.google.com/?q=Suwanee+Town+Center+GA',
    momTip: 'Grab lunch at one of the nearby restaurants while the kids splash.',
    tags: ['Free', 'Town Center', 'Water Feature', 'Restaurants Nearby'],
  },
  // LIBRARIES
  {
    id: 'td-13',
    name: 'Sugar Hill Branch Library',
    category: 'libraries',
    location: 'Sugar Hill, GA',
    address: '5039 W Broad St, Sugar Hill, GA 30518',
    description:
      'Free story times, craft programs, STEM activities, and summer reading challenges. A wonderful resource for local families with programming for all ages.',
    ageRange: 'All ages',
    cost: 'Free',
    image:
      'https://readdy.ai/api/search-image?query=bright%20modern%20public%20library%20children%5Cs%20section%20colorful%20picture%20books%20low%20shelves%20reading%20nook%20bean%20bag%20chairs%20cheerful%20welcoming%20atmosphere%20natural%20light%20educational%20community%20space&width=800&height=520&seq=ttd_l1&orientation=landscape',
    mapLink: 'https://maps.google.com/?q=Gwinnett+County+Public+Library+Sugar+Hill+GA',
    website: 'https://www.gwinnettpl.org',
    momTip: 'Tuesday morning story time is a local favorite. Free and totally worth it.',
    tags: ['Story Time', 'Free Programs', 'STEM', 'Summer Reading'],
  },
  {
    id: 'td-14',
    name: 'Lawrenceville Branch Library',
    category: 'libraries',
    location: 'Lawrenceville, GA',
    address: '1001 Sullivan Rd, Lawrenceville, GA 30046',
    description:
      'The main county library branch with extensive children\'s programming, a dedicated youth section, maker space, and regular community events for families.',
    ageRange: 'All ages',
    cost: 'Free',
    image:
      'https://readdy.ai/api/search-image?query=large%20public%20library%20building%20interior%20children%20section%20colorful%20activity%20area%20parents%20and%20kids%20reading%20together%20modern%20well%20lit%20educational%20facility%20community%20gathering%20bright&width=800&height=520&seq=ttd_l2&orientation=landscape',
    mapLink: 'https://maps.google.com/?q=Gwinnett+County+Public+Library+Lawrenceville+GA',
    website: 'https://www.gwinnettpl.org',
    momTip: 'Has a maker space with 3D printers — great for older kids.',
    tags: ['Maker Space', 'Story Time', 'Large Collection', 'Kids Programs'],
  },
  {
    id: 'td-15',
    name: 'Suwanee Branch Library',
    category: 'libraries',
    location: 'Suwanee, GA',
    address: '361 Main St, Suwanee, GA 30024',
    description:
      'Cozy community library with excellent children\'s programs, weekly story times, and a warm staff that makes every visit welcoming for young readers.',
    ageRange: 'All ages',
    cost: 'Free',
    image:
      'https://readdy.ai/api/search-image?query=cozy%20community%20library%20branch%20warm%20interior%20wooden%20bookshelves%20colorful%20children%20section%20staff%20helping%20family%20comfortable%20reading%20chairs%20welcoming%20neighborhood%20library%20atmosphere&width=800&height=520&seq=ttd_l3&orientation=landscape',
    mapLink: 'https://maps.google.com/?q=Gwinnett+County+Public+Library+Suwanee+GA',
    website: 'https://www.gwinnettpl.org',
    momTip: 'Check the online events calendar — programming fills up fast.',
    tags: ['Story Time', 'Community Programs', 'Cozy', 'Kids Friendly'],
  },
  // FAMILY FUN
  {
    id: 'td-16',
    name: 'Suwanee Town Center',
    category: 'family-fun',
    location: 'Suwanee, GA',
    address: '330 Town Center Ave, Suwanee, GA 30024',
    description:
      'A vibrant outdoor town center with an amphitheater, restaurants, shops, open lawns, and regular free family events. A go-to destination for easy family outings.',
    ageRange: 'All ages',
    cost: 'Free to visit',
    image:
      'https://readdy.ai/api/search-image?query=charming%20town%20center%20with%20amphitheater%20open%20green%20lawn%20string%20lights%20surrounding%20restaurants%20families%20sitting%20on%20grass%20children%20playing%20outdoor%20community%20gathering%20place%20summer%20evening%20Georgia%20town&width=800&height=520&seq=ttd_f1&orientation=landscape',
    mapLink: 'https://maps.google.com/?q=Suwanee+Town+Center+GA',
    momTip: 'Friday evening concerts in summer are a local tradition.',
    tags: ['Amphitheater', 'Restaurants', 'Free Events', 'Outdoor'],
  },
  {
    id: 'td-17',
    name: 'Andretti Indoor Karting',
    category: 'family-fun',
    location: 'Roswell, GA',
    address: '600 Expo Pkwy, Marietta, GA 30062',
    description:
      'High-speed electric go-karts, laser tag, bowling, arcade games, and a full restaurant. A premium family entertainment experience for older kids and parents who want to race.',
    ageRange: 'Ages 7 and up',
    cost: 'Paid',
    image:
      'https://readdy.ai/api/search-image?query=indoor%20go%20kart%20racing%20facility%20with%20electric%20karts%20on%20multi%20level%20track%20exciting%20colorful%20lighting%20racers%20wearing%20helmets%20modern%20entertainment%20center%20family%20activity%20exciting%20atmosphere&width=800&height=520&seq=ttd_f2&orientation=landscape',
    mapLink: 'https://maps.google.com/?q=Andretti+Indoor+Karting+Marietta+GA',
    website: 'https://andrettikarting.com',
    momTip: 'Kids must meet height requirements. Great for 8+ birthday parties.',
    tags: ['Go-Karts', 'Bowling', 'Arcade', 'Restaurant', 'Birthday Parties'],
  },
  {
    id: 'td-18',
    name: 'Dunwoody Nature Center',
    category: 'family-fun',
    location: 'Dunwoody, GA',
    address: '5343 Roberts Dr, Dunwoody, GA 30338',
    description:
      'Nature trails, creek exploration, wildlife habitats, and regular guided outdoor programs. Perfect for teaching kids about the environment in a hands-on way.',
    ageRange: 'All ages',
    cost: 'Free / Low cost',
    image:
      'https://readdy.ai/api/search-image?query=nature%20center%20with%20wooded%20trails%20creek%20exploration%20children%20learning%20about%20wildlife%20with%20educator%20outdoor%20education%20hands%20on%20nature%20experience%20Georgia%20woodland%20stream%20rocks%20logs&width=800&height=520&seq=ttd_f3&orientation=landscape',
    mapLink: 'https://maps.google.com/?q=Dunwoody+Nature+Center+GA',
    website: 'https://dunwoodynature.org',
    momTip: 'The creek programs are incredible. Book seasonal workshops early.',
    tags: ['Nature Trails', 'Creek', 'Wildlife', 'Outdoor Education'],
  },
];

export const categoryOrder: ThingsToDoCategory[] = [
  'parks',
  'indoor-play',
  'splash-pads',
  'libraries',
  'family-fun',
];
